/**
 * Extension Bridge
 * Communicates with Specuno Chrome Extension for unified SSO
 * Uses chrome.runtime.sendMessage via externally_connectable
 */

// Known extension IDs (production and development)
const EXTENSION_IDS = [
  // Production extension ID (from Chrome Web Store)
  'YOUR_PRODUCTION_EXTENSION_ID',
  // Development extension ID (changes per developer)
  // Will be detected dynamically
];

// Cache for detected extension ID
let detectedExtensionId: string | null = null;

interface ExtensionAuthState {
  isAuthenticated: boolean;
  user: {
    id: string;
    email: string;
    fullName: string;
  } | null;
  backofficeUrl: string | null;
}

interface ExtensionCheckResponse {
  success: boolean;
  installed?: boolean;
  authenticated?: boolean;
  user?: {
    id: string;
    email: string;
    fullName: string;
  } | null;
  error?: string;
}

interface ExtensionAuthStateResponse {
  success: boolean;
  authState?: ExtensionAuthState;
  token?: string;
  error?: string;
}

/**
 * Check if Chrome extension APIs are available
 */
function isChromeExtensionAvailable(): boolean {
  return typeof chrome !== 'undefined' && 
         chrome.runtime && 
         typeof chrome.runtime.sendMessage === 'function';
}

/**
 * Send message to extension
 */
async function sendToExtension<T = any>(
  extensionId: string, 
  message: any
): Promise<T | null> {
  if (!isChromeExtensionAvailable()) {
    return null;
  }
  
  return new Promise((resolve) => {
    try {
      chrome.runtime.sendMessage(extensionId, message, (response) => {
        if (chrome.runtime.lastError) {
          console.log('[ExtensionBridge] Communication error:', chrome.runtime.lastError.message);
          resolve(null);
        } else {
          resolve(response);
        }
      });
      
      // Timeout after 3 seconds
      setTimeout(() => resolve(null), 3000);
    } catch (error) {
      console.log('[ExtensionBridge] Send error:', error);
      resolve(null);
    }
  });
}

/**
 * Detect installed extension ID by trying to communicate
 */
async function detectExtensionId(): Promise<string | null> {
  if (detectedExtensionId) {
    return detectedExtensionId;
  }
  
  if (!isChromeExtensionAvailable()) {
    return null;
  }
  
  // Try each known extension ID
  for (const extensionId of EXTENSION_IDS) {
    if (extensionId === 'YOUR_PRODUCTION_EXTENSION_ID') continue; // Skip placeholder
    
    const response = await sendToExtension<{ success: boolean; extensionId?: string }>(
      extensionId,
      { type: 'SPECUNO_GET_EXTENSION_ID' }
    );
    
    if (response?.success) {
      detectedExtensionId = extensionId;
      console.log('[ExtensionBridge] Detected extension:', extensionId);
      return extensionId;
    }
  }
  
  // If no known ID works, try to detect via content script postMessage
  // This is a fallback mechanism
  return null;
}

/**
 * Check if extension is installed and authenticated
 */
export async function checkExtensionAuth(): Promise<ExtensionCheckResponse> {
  const extensionId = await detectExtensionId();
  
  if (!extensionId) {
    // Try to detect via postMessage to content script
    return new Promise((resolve) => {
      // Set up listener for response
      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'SPECUNO_AUTH_CHECK_RESPONSE') {
          window.removeEventListener('message', handler);
          resolve({
            success: true,
            installed: true,
            authenticated: event.data.authenticated,
            user: event.data.user,
          });
        }
      };
      
      window.addEventListener('message', handler);
      
      // Send request via postMessage (content script will forward)
      window.postMessage({ type: 'SPECUNO_AUTH_CHECK_REQUEST' }, '*');
      
      // Timeout
      setTimeout(() => {
        window.removeEventListener('message', handler);
        resolve({ success: false, installed: false });
      }, 2000);
    });
  }
  
  const response = await sendToExtension<ExtensionCheckResponse>(
    extensionId,
    { type: 'SPECUNO_AUTH_CHECK' }
  );
  
  if (response?.success) {
    return response;
  }
  
  return { success: false, installed: false };
}

/**
 * Sync login state to extension
 */
export async function syncLoginToExtension(
  token: string,
  user: { id: string; email: string; fullName: string; isSuperAdmin?: boolean },
  backofficeUrl?: string
): Promise<boolean> {
  const extensionId = await detectExtensionId();
  
  if (!extensionId) {
    // Fallback: send via postMessage to content script
    window.postMessage({
      type: 'SPECUNO_AUTH_LOGIN_REQUEST',
      token,
      user,
      backofficeUrl: backofficeUrl || window.location.origin,
    }, '*');
    return true; // Assume success via postMessage
  }
  
  const response = await sendToExtension<{ success: boolean }>(
    extensionId,
    {
      type: 'SPECUNO_AUTH_LOGIN',
      token,
      user,
      backofficeUrl: backofficeUrl || window.location.origin,
    }
  );
  
  return response?.success ?? false;
}

/**
 * Sync logout to extension
 */
export async function syncLogoutToExtension(): Promise<boolean> {
  const extensionId = await detectExtensionId();
  
  if (!extensionId) {
    // Fallback: send via postMessage to content script
    window.postMessage({ type: 'SPECUNO_AUTH_LOGOUT_REQUEST' }, '*');
    return true;
  }
  
  const response = await sendToExtension<{ success: boolean }>(
    extensionId,
    { type: 'SPECUNO_AUTH_LOGOUT' }
  );
  
  return response?.success ?? false;
}

/**
 * Get auth state from extension (to auto-login in backoffice)
 */
export async function getExtensionAuthState(): Promise<ExtensionAuthStateResponse | null> {
  const extensionId = await detectExtensionId();
  
  if (!extensionId) {
    // Fallback: try via postMessage
    return new Promise((resolve) => {
      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'SPECUNO_AUTH_STATE_RESPONSE') {
          window.removeEventListener('message', handler);
          resolve({
            success: true,
            authState: event.data.authState,
            token: event.data.token,
          });
        }
      };
      
      window.addEventListener('message', handler);
      window.postMessage({ type: 'SPECUNO_AUTH_GET_STATE_REQUEST' }, '*');
      
      setTimeout(() => {
        window.removeEventListener('message', handler);
        resolve(null);
      }, 2000);
    });
  }
  
  return sendToExtension<ExtensionAuthStateResponse>(
    extensionId,
    { type: 'SPECUNO_AUTH_GET_STATE' }
  );
}

/**
 * Set the extension ID manually (useful for development)
 */
export function setExtensionId(id: string): void {
  detectedExtensionId = id;
  if (!EXTENSION_IDS.includes(id)) {
    EXTENSION_IDS.push(id);
  }
}

/**
 * Check if extension is installed (quick check)
 */
export async function isExtensionInstalled(): Promise<boolean> {
  const result = await checkExtensionAuth();
  return result.installed ?? false;
}
